import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { useAlert } from "react-alert";

import { AuctionSetupCreate } from "../../../adminModel/auctions";


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: "20px 24px 16px",
  },

  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#202124",
  },

  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: "#6b7280",
  },

  content: {
    padding: "24px",
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 18,
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 7,
    },

    "& .MuiInputBase-input": {
      fontSize: 14,
    },

    "& .MuiInputLabel-root": {
      fontSize: 14,
    },
  },

  actions: {
    padding: "14px 24px",
    backgroundColor: "#fafafa",
  },

  cancelButton: {
    textTransform: "none",
    minWidth: 90,
  },

  createButton: {
    textTransform: "none",
    minWidth: 110,
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },
}));


// =====================================================
// COMPONENT
// =====================================================

export default function AuctionSetupUpdate(props) {
  const classes = useStyles();
  const alert = useAlert();

  const {
    statusSetup,
    onCloseSetup,
    dataData,
  } = props;


  // =====================================================
  // STATE
  // =====================================================

  const [values, setValues] = useState({
    set_name: "",
    date_start: "",
    time_start: "",
    date_end: "",
    time_end: "",
    isLive: null,
    isShow: null,
  });

  const [loading, setLoading] = useState(false);


  // =====================================================
  // HANDLE CHANGE
  // =====================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  // =====================================================
  // CLOSE
  // =====================================================

  const handleClose = () => {
    if (loading) return;

    onCloseSetup(false);
  };


  // =====================================================
  // CREATE SETUP
  // =====================================================

  const handleClickCreate = async (event) => {
    event.preventDefault();

    // -----------------------------
    // Basic validation
    // -----------------------------

    if (!values.set_name.trim()) {
      alert.error("Setup name is required.");
      return;
    }

    if (!values.date_start || !values.time_start) {
      alert.error("Start date and time are required.");
      return;
    }

    if (!values.date_end || !values.time_end) {
      alert.error("End date and time are required.");
      return;
    }


    // -----------------------------
    // Prepare data
    // -----------------------------

    const ArrayData = {
      auction_id: dataData,
      set_name: values.set_name,
      date_start: values.date_start,
      time_start: values.time_start,
      date_end: values.date_end,
      time_end: values.time_end,
    };


    // -----------------------------
    // Submit
    // -----------------------------

    setLoading(true);

    try {
      // Keep existing API process
      const result = await AuctionSetupCreate(ArrayData);

      alert.success(result);

      onCloseSetup(false);

      // Keep existing reload process
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error("AuctionSetupCreate error:", error);

      alert.error(
        error?.response?.data?.msg ||
        "Unable to create auction setup."
      );

    } finally {
      setLoading(false);
    }
  };


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={Boolean(statusSetup)}
      onClose={handleClose}
      aria-labelledby="create-setup-title"
    >

      {/* HEADER */}

      <DialogTitle
        id="create-setup-title"
        className={classes.dialogTitle}
      >
        <Typography className={classes.title}>
          Create Auction Setup
        </Typography>

        <Typography className={classes.subtitle}>
          Configure the schedule for this auction.
        </Typography>
      </DialogTitle>

      <Divider />


      {/* CONTENT */}

      <DialogContent className={classes.content}>

        <Typography className={classes.sectionTitle}>
          Setup Information
        </Typography>

        <Grid container spacing={3}>

          {/* SETUP NAME */}

          <Grid item xs={12}>
            <TextField
              className={classes.field}
              fullWidth
              required
              label="Setup Name"
              name="set_name"
              type="text"
              variant="outlined"
              value={values.set_name}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>


          {/* START DATE */}

          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.field}
              fullWidth
              required
              label="Start Date"
              name="date_start"
              type="date"
              variant="outlined"
              value={values.date_start}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Grid>


          {/* START TIME */}

          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.field}
              fullWidth
              required
              label="Start Time"
              name="time_start"
              type="time"
              variant="outlined"
              value={values.time_start}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Grid>


          {/* END DATE */}

          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.field}
              fullWidth
              required
              label="End Date"
              name="date_end"
              type="date"
              variant="outlined"
              value={values.date_end}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Grid>


          {/* END TIME */}

          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.field}
              fullWidth
              required
              label="End Time"
              name="time_end"
              type="time"
              variant="outlined"
              value={values.time_end}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Grid>

        </Grid>

      </DialogContent>


      {/* ACTIONS */}

      <Divider />

      <DialogActions className={classes.actions}>

        <Button
          className={classes.cancelButton}
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          className={classes.createButton}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleClickCreate}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create Setup"}
        </Button>

      </DialogActions>

    </Dialog>
  );
}